package com.advaita.cloud.drive.shared

import kotlinx.serialization.EncodeDefault
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable

/**
 * ADV-1326c: the person's standing in DataHub, from UserVault's answer to the sign-in report
 * (ADV-1326b, `admission: {status, baseRole, ownSpace}`). `/api/me` carries it as
 * [UserInfoDto.admission].
 *
 * Every field is required, so each one is always on the wire. [status] is a string, not an enum,
 * so a frontend reads a status it does not know without failing: only [PENDING], [DENIED] and
 * [REMOVED] change what it shows.
 *
 * @param status [ADMITTED], [PENDING], [DENIED] or [REMOVED].
 * @param baseRole the base role's name, or null for none. Null for anybody not [ADMITTED].
 * @param ownSpace the base role's capabilities after the org's ceilings. Empty for anybody not
 *   [ADMITTED], and for a guest.
 */
@Serializable
data class AdmissionDto(
    val status: String,
    val baseRole: String?,
    val ownSpace: List<String>,
) {
    companion object {
        const val ADMITTED = "ADMITTED"
        const val PENDING = "PENDING"
        const val DENIED = "DENIED"
        const val REMOVED = "REMOVED"
    }
}

/**
 * ADV-1326c (RULING 2026-09-28 (1)): the answer of `GET /api/me/admission`, the standing the pod
 * holds for the caller. It reads the pod's memory only. The web asks once, about 2 s after a
 * first `/api/me` that answered `admission: null`. Example: Acme turns auto_admission off, and Sam
 * signs in. His `/api/me` answers null, and the poll 2 s later answers `PENDING`.
 *
 * [admission] has the meaning of [UserInfoDto.admission], null included. `@EncodeDefault` puts
 * the null on the wire, and a body without the key reads as null.
 */
@OptIn(ExperimentalSerializationApi::class)
@Serializable
data class MeAdmissionDto(
    @EncodeDefault(EncodeDefault.Mode.ALWAYS)
    val admission: AdmissionDto? = null,
)
